/**
 * Helpers for dishaChat message shapes — especially task cards persisted in the thread.
 */

export function getParticipantUserId(participant) {
  return participant?.userId || participant?.user?.id || participant?.id || null;
}

export function getParticipantRole(participant) {
  const role = (participant?.role || participant?.participantRole || participant?.user?.role || '')
    .toString()
    .toLowerCase();
  if (role === 'admin' || role === 'owner') return 'admin';
  return role || 'member';
}

export function isParticipantAdmin(participant) {
  return getParticipantRole(participant) === 'admin';
}

export function isCurrentUserGroupAdmin(conversation, currentUserId) {
  if (!conversation || conversation.type !== 'group') return false;
  const participants = conversation.participants || [];

  if (conversation.createdBy && String(conversation.createdBy) === String(currentUserId)) {
    return true;
  }
  if (conversation.ownerId && String(conversation.ownerId) === String(currentUserId)) {
    return true;
  }

  const hasAnyRoleField = participants.some(p => p?.role || p?.participantRole || p?.user?.role);

  if (!hasAnyRoleField) {
    return participants.some(p => String(getParticipantUserId(p)) === String(currentUserId));
  }

  const me = participants.find(p => String(getParticipantUserId(p)) === String(currentUserId));
  if (!me) return false;
  return isParticipantAdmin(me);
}

export function normalizeChatMessage(message) {
  if (!message || typeof message !== 'object') return message;

  const metadata = message.metadata || message.meta || {};
  const nestedTask = message.task || metadata.task || null;
  const isTask =
    message.type === 'task' ||
    message.contentType === 'task' ||
    metadata.type === 'task' ||
    Boolean(nestedTask) ||
    Boolean(metadata.ticketId || metadata.taskId);

  if (!isTask) {
    const isSystem =
      message.type === 'system' || message.contentType === 'system' || metadata.type === 'system';

    if (isSystem) {
      return {
        ...message,
        type: 'system',
        parentMessage: null,
        replyTo: null
      };
    }

    return {
      ...message,
      isDeleted: Boolean(
        message.isDeleted ||
          message.deleted ||
          message.deletedAt ||
          metadata.isDeleted ||
          metadata.deleted
      ),
      parentMessage: message.parentMessage || message.replyTo || null,
      forwardedFromMessage:
        message.forwardedFromMessage ||
        message.forwardedFrom ||
        (message.forwardedFromMessageId
          ? {
              id: message.forwardedFromMessageId,
              conversationId: message.forwardedFromConversationId,
              body: message.forwardedFromBody || message.body,
              sender: message.forwardedFromSender || null
            }
          : null)
    };
  }

  const ticketId =
    nestedTask?.id || metadata.ticketId || metadata.taskId || message.ticketId || null;

  const title =
    nestedTask?.title ||
    nestedTask?.ticketName ||
    metadata.title ||
    metadata.ticketName ||
    (typeof message.body === 'string' && message.body.startsWith('Task created')
      ? message.body.replace(/^Task created:?\s*/i, '')
      : message.body) ||
    `Task #${ticketId || ''}`;

  return {
    ...message,
    type: 'task',
    body: message.body || 'Task created',
    task: {
      id: ticketId,
      title,
      status: nestedTask?.status || metadata.status || 'Todo',
      assignee:
        nestedTask?.assignee ||
        nestedTask?.assigneeName ||
        metadata.assignee ||
        metadata.assigneeName ||
        null,
      url: nestedTask?.url || metadata.url || (ticketId ? `/tasks/${ticketId}` : null)
    }
  };
}

export function dedupeTaskMessages(messages = []) {
  const seenTaskIds = new Set();
  const result = [];

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.type === 'task' && msg?.task?.id != null) {
      const key = String(msg.task.id);
      if (!seenTaskIds.has(key)) {
        seenTaskIds.add(key);
        result.unshift(msg);
      }
    } else {
      result.unshift(msg);
    }
  }

  return result;
}

export function normalizeChatMessages(messages) {
  if (!Array.isArray(messages)) return messages;
  const normalized = messages.map(normalizeChatMessage);
  return dedupeTaskMessages(normalized);
}

export function ticketToTaskChatMessage(ticket, conversationId) {
  if (!ticket?.id) return null;
  const assigneeName =
    ticket.assigneeName ||
    ticket.assignedToName ||
    (ticket.assignedToUser
      ? `${ticket.assignedToUser.firstName || ''} ${ticket.assignedToUser.lastName || ''}`.trim()
      : null) ||
    null;

  return normalizeChatMessage({
    id: `task-ticket-${ticket.id}`,
    conversationId,
    type: 'task',
    body: 'Task created',
    createdAt: ticket.createdAt || ticket.ref?.createdAt || ticket.updatedAt || new Date().toISOString(),
    senderId: ticket.createdBy || ticket.createdById || null,
    sender: ticket.createdByUser || null,
    task: {
      id: ticket.id,
      title: ticket.ticketName || ticket.title || `Task #${ticket.id}`,
      status: ticket.status || 'Todo',
      assignee: assigneeName,
      url: `/tasks/${ticket.id}`
    },
    metadata: {
      type: 'task',
      ticketId: ticket.id,
      source: 'ticket-fallback'
    }
  });
}

export function ticketBelongsToConversation(ticket, conversationId) {
  if (!ticket || conversationId == null || conversationId === '') return false;
  const convId = String(conversationId);

  const service = (
    ticket.serviceAssociatedWith ||
    ticket.service ||
    ticket.ref?.taskType ||
    ''
  )
    .toString()
    .toLowerCase();

  if (service && service !== 'airachat' && service !== 'chat' && service !== 'dishachat') {
    return false;
  }

  const explicitConversationIds = [
    ticket.ref?.conversationId,
    ticket.itemId,
    ticket.metadata?.conversationId,
    ticket.chatConversationId,
    ticket.conversationId
  ];

  if (explicitConversationIds.some(id => id != null && String(id) === convId)) {
    return true;
  }

  const entity = (ticket.associatedWithEntity || ticket.ref?.associatedWithEntity || '')
    .toString()
    .toLowerCase();
  const entityId = ticket.associatedWithEntityId;
  if (entityId == null || entityId === '') return false;

  if (!entity || entity === 'conversation') {
    return String(entityId) === convId;
  }

  if (entity === 'message') {
    if (String(entityId) === convId) return true;
    const messageId = ticket.ref?.messageId;
    if (messageId != null && String(entityId) === String(messageId)) {
      return false;
    }
    return false;
  }

  return String(entityId) === convId;
}

export function mergeTaskMessagesFromTickets(messages, tickets, conversationId) {
  const normalized = normalizeChatMessages(messages || []);
  const existingTicketIds = new Set(
    normalized.filter(m => m?.type === 'task' && m?.task?.id).map(m => String(m.task.id))
  );

  const extras = (tickets || [])
    .filter(t => ticketBelongsToConversation(t, conversationId))
    .map(t => ticketToTaskChatMessage(t, conversationId))
    .filter(m => m && !existingTicketIds.has(String(m.task.id)));

  if (!extras.length) return normalized;

  return [...normalized, ...extras].sort(
    (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
  );
}

export function buildTaskChatMessagePayload({
  conversationId,
  ticket,
  title,
  status = 'Todo',
  assigneeName = null,
  sourceMessageId = null
}) {
  const ticketId = ticket?.id;
  const taskTitle = ticket?.ticketName || title;
  const task = {
    id: ticketId,
    title: taskTitle,
    status: ticket?.status || status,
    assignee: assigneeName,
    url: ticketId ? `/tasks/${ticketId}` : null
  };

  return {
    conversationId,
    body: `Task created: ${taskTitle}`,
    type: 'task',
    contentType: 'task',
    metadata: {
      type: 'task',
      ticketId,
      taskId: ticketId,
      title: taskTitle,
      status: task.status,
      assignee: assigneeName,
      assigneeName,
      url: task.url,
      sourceMessageId,
      task
    },
    task
  };
}

export function getChatApiErrorMessage(error, fallback = 'Something went wrong') {
  if (!error) return fallback;
  if (typeof error === 'string') return error;

  return (
    error?.message ||
    error?.data?.message ||
    error?.payload?.message ||
    error?.response?.data?.message ||
    error?.error?.details?.message ||
    error?.data?.error?.details?.message ||
    error?.response?.data?.error?.details?.message ||
    fallback
  );
}

/** Org setting General.enable_ask_aira (valueBoolean) — same pattern as aira-admin */
export function isDishaChatEnabled(user, configEnable) {
  if (configEnable === true) return true;

  const settingSources = [
    user?.organization?.settings,
    user?.orgSettings,
    user?.settings,
    user?.organizationSettings
  ].filter(Boolean);

  const hasAskAiraEnabled = settingSources.some(settingList => {
    if (!Array.isArray(settingList)) return false;
    const general = settingList.find(h => String(h?.heading || '').toLowerCase() === 'general');
    const item = (general?.subCards || general?.items || settingList).find(
      i => String(i?.key || '').toLowerCase() === 'enable_ask_aira'
    );
    return Boolean(item && item.valueBoolean === true);
  });
  if (hasAskAiraEnabled) return true;

  // Honor team permission key `airachat` when present on user roles/permissions
  const perms = user?.permissions || user?.customAccess || [];
  if (Array.isArray(perms) && perms.some(p => String(p).toLowerCase() === 'airachat')) {
    return true;
  }
  if (user?.features?.airachat || user?.featureFlags?.airachat) return true;

  return false;
}
